<script setup lang="ts">
  import {
    computed,
    nextTick,
    onActivated,
    onBeforeUnmount,
    onMounted,
    reactive,
    ref,
    watch,
  } from 'vue';
  import {
    actions as actionTypes,
    useUserComponent,
    useHelpers,
    createComponentScope,
    Resource,
    uniqueID,
  } from '@integro/core';
  import type { IAppHelpers } from '@integro/core';
  import * as _ from 'lodash';

  defineOptions({
    name: 'ProductPrice',
  });

  const componentMetadata = {
    kind: 'page',
  };

  const emit = defineEmits(['reload', 'close', 'save']);

  const props = defineProps({
    oid: {
      type: String,
      required: false,
      default: null,
    },
    product: {
      type: Object,
      required: false,
      default: null,
    },
    is_purchase_price: {
      type: Boolean,
      required: false,
    },
    modal: {
      type: Boolean,
      required: false,
      default: false,
    },
    modalName: {
      type: String,
      required: false,
    },
  });

  const {
    $app,
    $fn,
    $i18n,
    $store,
    $config,
    $imodal,
    $notification,
    $tabs,
    $route,
    $router,
    gettext,
    pgettext,
    ngettext,
    npgettext,
    schemaDefaults,
    getSchema,
    searchDataSource,
    randomElementId,
    datetime,
    date,
    time,
    number,
    money,
    distance,
    username,
    filesize,
  } = useHelpers();

  const item = ref(null);
  const currencies = ref(null);
  const tariffs = ref(null);
  const formName = ref('productService');
  const formId = ref(null);
  const labelAlign = ref('right');
  const isReadOnly = ref(null);
  const ux = ref([]);
  const b_7315092382398562_noShow = computed(() => false);
  const b_7315092382398562_modalBindings = computed(() => ({
    size: 'md',
  }));
  const b_1879559807163442_dataSource = computed(() => ({
    name: 'suggest_company',
    parameters: {
      role: 'partner',
    },
  }));

  const $data = computed(() => ({
    item: item.value,
    currencies: currencies.value,
    tariffs: tariffs.value,
    formName: formName.value,
    formId: formId.value,
    labelAlign: labelAlign.value,
    isReadOnly: isReadOnly.value,
    ux: ux.value,
    b_7315092382398562_noShow: b_7315092382398562_noShow.value,
    b_7315092382398562_modalBindings: b_7315092382398562_modalBindings.value,
    b_1879559807163442_dataSource: b_1879559807163442_dataSource.value,
  }));

  const $$load_item = async () => {
    return props.oid
      ? $fn.value.fetch('read_product_price', { parameters: { id: props.oid } })
      : $fn.value.schemaDefaults('CreateProductPrice', {
          product_id: props.product.id,
          is_purchase_price: props.is_purchase_price,
        });
  };
  const $$load_currencies = async () => {
    return $fn.value.fetch('list_currency');
  };
  const $$load_tariffs = async () => {
    return $app.value.repo.get('list_object_by_type', {
      parameters: { object_type: 'tariff' },
    });
  };
  const $$load_formId = async () => {
    return $fn.value.randomElementId();
  };
  const $$load_isReadOnly = async () => {
    return item.value.locked;
  };

  const {
    loading,
    error,
    loaded,
    reloading,
    showLoadingIndicator,
    loadDataThrottled,
    friendlyName,
    loadData,
    touchLastLoaded,
    handleError,
    setData,
    forms,
    registerForm,
    unregisterForm,
    canLoadData,
    isModalComponent,
    modalName,
    pageDocument: _pageDocument,
    routeTab,
    ux: _ux,
    componentMetadata: _componentMetadata,
    $uid,
    lastLoaded,
  } = useUserComponent(props, {
    metadata: componentMetadata,
    dataSpecs: [
      { name: 'item' },
      { name: 'currencies' },
      { name: 'tariffs' },
      { name: 'formName' },
      { name: 'formId' },
      { name: 'labelAlign' },
      { name: 'isReadOnly', dependsOn: ['item'] },
      { name: 'ux' },
      { name: 'b_7315092382398562_noShow', nodeId: '7315092382398562' },
      { name: 'b_7315092382398562_modalBindings', nodeId: '7315092382398562' },
      { name: 'b_1879559807163442_dataSource', nodeId: '1879559807163442' },
    ],
    loaders: {
      item: $$load_item,
      currencies: $$load_currencies,
      tariffs: $$load_tariffs,
      formId: $$load_formId,
      isReadOnly: $$load_isReadOnly,
    },
    ux: {},
    dataRefs: {
      item,
      currencies,
      tariffs,
      formName,
      formId,
      labelAlign,
      isReadOnly,
      ux,
      b_7315092382398562_noShow,
      b_7315092382398562_modalBindings,
      b_1879559807163442_dataSource,
    },
  });

  const $component = createComponentScope(
    {
      $app,
      $fn,
      $i18n,
      $store,
      $imodal,
      $notification,
      $tabs,
      $router,
    } as IAppHelpers,
    {
      emit,
      forms,
      error,
      isModalComponent,
      modalName,
      componentMetadata,
      loadData,
      registerForm,
      unregisterForm,
      setData,
      touchLastLoaded,
      handleError,
    }
  );

  const pageDocument = computed(() => {
    return {
      title: props.oid
        ? $fn.value.gettext('Edit Price')
        : $fn.value.gettext('Add Price'),
    };
  });
  const documentTitle = computed(() => {
    return pageDocument.value
      ? Array.isArray(pageDocument.value.title)
        ? pageDocument.value.title.filter(Boolean).join(' ')
        : pageDocument.value.title
      : null;
  });

  watch(
    () => props.oid,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('oid');
    }
  );
  watch(
    () => props.product,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('product');
    }
  );
  watch(
    () => props.is_purchase_price,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('is_purchase_price');
    }
  );
  watch(
    () => props.modal,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('modal');
    }
  );
  watch(
    () => props.modalName,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('modalName');
    }
  );

  watch(
    pageDocument,
    (newDoc) => {
      if (newDoc) {
        _pageDocument.value = newDoc;
      }
    },
    { immediate: true }
  );

  const act_7315092382398562_closeModal_ycovqlrql_getActionArgs = async (
    $event
  ) => {
    return {
      name: props.modalName,
      result: {
        close: true,
        cancel: true,
      },
    };
  };
  const act_1040167445267876_closeModal_w6qjffo0v_getActionArgs = async (
    $event
  ) => {
    return {
      name: props.modalName,
      result: {
        ok: true,
        value: $event.data.response.data,
      },
    };
  };
  const act_1040167445267876_request_t5rdwvjlf_getActionArgs = async (
    $event
  ) => {
    return {
      operation: item.value.id
        ? 'update_product_price'
        : 'create_product_price',
      data: item.value,
    };
  };
  const act_3322283983417540_closeModal_b497qwowt_getActionArgs = async (
    $event
  ) => {
    return {
      result: {
        ok: true,
      },
    };
  };
  const act_5419443714917073_closeModal_nuujr6bm3_getActionArgs = async (
    $event
  ) => {
    return {
      result: {
        ok: true,
      },
    };
  };
  const act_2167826738365259_closeModal_nkw30s4xk_getActionArgs = async (
    $event
  ) => {
    return {
      name: props.modalName,
      result: {
        cancel: true,
      },
    };
  };

  const act_7315092382398562_closeModal_ycovqlrql =
    new actionTypes.CloseModalAction({
      actionArgs: act_7315092382398562_closeModal_ycovqlrql_getActionArgs,
    });
  const inst_evh_7315092382398562_close_t5668g6z2 =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_closeModal_ycovqlrql,
      event: 'close',
      displayName: 'closeModal',
    });
  const inst_evh_7315092382398562_close = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_close_t5668g6z2],
    event: 'close',
  });
  const act_7315092382398562_reloadComponentData_2qaxztqh4 =
    new actionTypes.ReloadComponentDataAction({});
  const inst_evh_7315092382398562_reload_eh3qsa6uv =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_reloadComponentData_2qaxztqh4,
      event: 'reload',
      displayName: 'reloadComponentData',
    });
  const inst_evh_7315092382398562_reload = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_reload_eh3qsa6uv],
    event: 'reload',
  });
  const act_8564662037462133_closeComponent_ypwa9tbh6 =
    new actionTypes.CloseComponentAction({});
  const inst_evh_8564662037462133_close_pgtzwtkai =
    new actionTypes.EventHandlerImpl({
      action: act_8564662037462133_closeComponent_ypwa9tbh6,
      event: 'close',
      displayName: 'closeComponent',
    });
  const inst_evh_8564662037462133_close = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_8564662037462133_close_pgtzwtkai],
    event: 'close',
  });
  const act_1040167445267876_closeModal_w6qjffo0v =
    new actionTypes.CloseModalAction({
      actionArgs: act_1040167445267876_closeModal_w6qjffo0v_getActionArgs,
    });
  const inst_evh_1040167445267876_success_g40pz64f1 =
    new actionTypes.EventHandlerImpl({
      action: act_1040167445267876_closeModal_w6qjffo0v,
      event: 'success',
      displayName: 'closeModal',
    });
  const act_1040167445267876_request_t5rdwvjlf = new actionTypes.RequestAction({
    actionArgs: act_1040167445267876_request_t5rdwvjlf_getActionArgs,
    displayName: 'submit',
    events: [inst_evh_1040167445267876_success_g40pz64f1],
  });
  const inst_evh_1040167445267876_submit_0krk7jxm3 =
    new actionTypes.EventHandlerImpl({
      action: act_1040167445267876_request_t5rdwvjlf,
      event: 'submit',
      displayName: 'submit',
    });
  const inst_evh_1040167445267876_submit = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_1040167445267876_submit_0krk7jxm3],
    event: 'submit',
  });
  const act_3322283983417540_closeModal_b497qwowt =
    new actionTypes.CloseModalAction({
      actionArgs: act_3322283983417540_closeModal_b497qwowt_getActionArgs,
    });
  const inst_evh_3322283983417540_success_rg0evcybz =
    new actionTypes.EventHandlerImpl({
      action: act_3322283983417540_closeModal_b497qwowt,
      event: 'success',
      displayName: 'closeModal',
    });
  const inst_evh_3322283983417540_success = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_3322283983417540_success_rg0evcybz],
    event: 'success',
  });
  const act_5419443714917073_closeModal_nuujr6bm3 =
    new actionTypes.CloseModalAction({
      actionArgs: act_5419443714917073_closeModal_nuujr6bm3_getActionArgs,
    });
  const inst_evh_5419443714917073_success_5ir4uvxd8 =
    new actionTypes.EventHandlerImpl({
      action: act_5419443714917073_closeModal_nuujr6bm3,
      event: 'success',
      displayName: 'closeModal',
    });
  const inst_evh_5419443714917073_success = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_5419443714917073_success_5ir4uvxd8],
    event: 'success',
  });
  const act_2167826738365259_closeModal_nkw30s4xk =
    new actionTypes.CloseModalAction({
      actionArgs: act_2167826738365259_closeModal_nkw30s4xk_getActionArgs,
    });
  const inst_evh_2167826738365259_clickPreventStop_tslaws7hy =
    new actionTypes.EventHandlerImpl({
      action: act_2167826738365259_closeModal_nkw30s4xk,
      event: 'click.prevent.stop',
      displayName: 'closeModal',
    });
  const inst_evh_2167826738365259_clickPreventStop =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_2167826738365259_clickPreventStop_tslaws7hy],
      event: 'click.prevent.stop',
    });

  const actions = {
    inst_evh_7315092382398562_close_t5668g6z2,
    inst_evh_7315092382398562_close,
    inst_evh_7315092382398562_reload_eh3qsa6uv,
    inst_evh_7315092382398562_reload,
    inst_evh_8564662037462133_close_pgtzwtkai,
    inst_evh_8564662037462133_close,
    inst_evh_1040167445267876_success_g40pz64f1,
    inst_evh_1040167445267876_submit_0krk7jxm3,
    submit: inst_evh_1040167445267876_submit_0krk7jxm3,
    inst_evh_1040167445267876_submit,
    inst_evh_3322283983417540_success_rg0evcybz,
    inst_evh_3322283983417540_success,
    inst_evh_5419443714917073_success_5ir4uvxd8,
    inst_evh_5419443714917073_success,
    inst_evh_2167826738365259_clickPreventStop_tslaws7hy,
    inst_evh_2167826738365259_clickPreventStop,
  };

  const evh_7315092382398562_close_t5668g6z2 = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_close_t5668g6z2.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_close = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_close.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload_eh3qsa6uv = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload_eh3qsa6uv.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_8564662037462133_close_pgtzwtkai = async (event, scope) => {
    return await actions.inst_evh_8564662037462133_close_pgtzwtkai.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_8564662037462133_close = async (event, scope) => {
    return await actions.inst_evh_8564662037462133_close.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1040167445267876_success_g40pz64f1 = async (event, scope) => {
    return await actions.inst_evh_1040167445267876_success_g40pz64f1.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1040167445267876_submit_0krk7jxm3 = async (event, scope) => {
    return await actions.inst_evh_1040167445267876_submit_0krk7jxm3.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1040167445267876_submit = async (event, scope) => {
    return await actions.inst_evh_1040167445267876_submit.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_3322283983417540_success_rg0evcybz = async (event, scope) => {
    return await actions.inst_evh_3322283983417540_success_rg0evcybz.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_3322283983417540_success = async (event, scope) => {
    return await actions.inst_evh_3322283983417540_success.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_5419443714917073_success_5ir4uvxd8 = async (event, scope) => {
    return await actions.inst_evh_5419443714917073_success_5ir4uvxd8.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_5419443714917073_success = async (event, scope) => {
    return await actions.inst_evh_5419443714917073_success.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2167826738365259_clickPreventStop_tslaws7hy = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_2167826738365259_clickPreventStop_tslaws7hy.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2167826738365259_clickPreventStop = async (event, scope) => {
    return await actions.inst_evh_2167826738365259_clickPreventStop.executeFromDOM(
      $component,
      event,
      scope
    );
  };

  defineExpose({
    documentTitle,
    pageDocument: _pageDocument,
    routeTab,
    loadData,
    loading,
    loaded,
    error,
  });
</script>

<template>
  <ItpPage
    :header="documentTitle"
    :loaded="loaded"
    :noShow="b_7315092382398562_noShow"
    :loading="showLoadingIndicator"
    :reloading="reloading"
    :error="error"
    :modal="modal"
    :modalBindings="b_7315092382398562_modalBindings"
    itp-cname="ProductPrice"
    @close="evh_7315092382398562_close($event, {})"
    @reload="evh_7315092382398562_reload($event, {})"
  >
    <template #header>
      <UcHeader
        :card="!modal"
        :modal="modal"
        :title="documentTitle"
        v-if="loaded"
        @close="evh_8564662037462133_close($event, {})"
      >
      </UcHeader>
    </template>
    <UcBody :card="!modal" :modal="modal" v-if="loaded">
      <ItpForm
        :name="formName"
        :schema="oid == null ? 'CreateProductPrice' : 'UpdateProductPrice'"
        :id="formId"
        @submit="evh_1040167445267876_submit($event, {})"
      >
        <ItpFormGroup
          name="price"
          :label="pgettext('ProductPrice', 'Price')"
          :label-cols-md="3"
          :content-cols-md="6"
          :labelAlign="labelAlign"
          contentClass="input-group"
          required
        >
          <ItpFormInput
            type="number"
            name="price"
            v-model="item.price"
            :value="item.price"
            @input="
              !($event && typeof $event.stopPropagation === 'function') &&
              (item.price =
                $event && typeof $event === 'object' && 'data' in $event
                  ? $event.data
                  : $event)
            "
            required
            class="col-8"
          >
          </ItpFormInput>
          <ItpFormSelect2
            name="currency"
            v-model="item.currency"
            :value="item.currency"
            @input="
              !($event && typeof $event.stopPropagation === 'function') &&
              (item.currency =
                $event && typeof $event === 'object' && 'data' in $event
                  ? $event.data
                  : $event)
            "
            :options="currencies"
            textField="code"
            valueField="code"
            required
            class="col-4"
          >
          </ItpFormSelect2>
        </ItpFormGroup>
        <ItpFormGroup
          name="tariff_code"
          :label="pgettext('ProductPrice', 'Tariff')"
          :label-cols-md="3"
          :content-cols-md="6"
          :labelAlign="labelAlign"
          v-if="tariffs && tariffs.length"
        >
          <ItpFormSelect2
            name="tariff_code"
            v-model="item.tariff_code"
            :value="item.tariff_code"
            @input="
              !($event && typeof $event.stopPropagation === 'function') &&
              (item.tariff_code =
                $event && typeof $event === 'object' && 'data' in $event
                  ? $event.data
                  : $event)
            "
            :options="tariffs"
            clearable
            valueField="code"
            textField="code,name"
          >
          </ItpFormSelect2>
        </ItpFormGroup>
        <ItpFormGroup
          name="quantity_from"
          :label="pgettext('ProductPrice', 'From quantity')"
          :label-cols-md="3"
          :content-cols-md="6"
          :labelAlign="labelAlign"
          contentClass="input-group"
        >
          <ItpFormInput
            type="number"
            name="quantity_from"
            v-model="item.quantity_from"
            :value="item.quantity_from"
            @input="
              !($event && typeof $event.stopPropagation === 'function') &&
              (item.quantity_from =
                $event && typeof $event === 'object' && 'data' in $event
                  ? $event.data
                  : $event)
            "
            class="col-8"
          >
          </ItpFormInput>
          <ItpFormInput
            name="quantity_from_unit"
            v-model="product.unit"
            :value="product.unit"
            @input="
              !($event && typeof $event.stopPropagation === 'function') &&
              (product.unit =
                $event && typeof $event === 'object' && 'data' in $event
                  ? $event.data
                  : $event)
            "
            required
            readonly
            class="col-4 text-center"
          >
          </ItpFormInput>
        </ItpFormGroup>
        <ItpFormGroup
          name="valid_from"
          :label="pgettext('ProductPrice', 'Validity')"
          :label-cols-md="3"
          :content-cols-md="9"
          :labelAlign="labelAlign"
          contentClass="row"
        >
          <div class="col">
            <ItpFormDatetime
              type="date"
              name="valid_from"
              :placeholder="pgettext('InputPlaceholder', 'Valid from')"
              v-model="item.valid_from"
              :readonly="isReadOnly"
              required
            >
            </ItpFormDatetime>
          </div>
          <div class="col">
            <ItpFormDatetime
              type="date"
              name="valid_to"
              :placeholder="pgettext('InputPlaceholder', 'Valid to')"
              v-model="item.valid_to"
              :readonly="isReadOnly"
            >
            </ItpFormDatetime>
          </div>
        </ItpFormGroup>
        <ItpFormGroup
          name="partner"
          :label="pgettext('ProductPrice', 'Client')"
          :label-cols-md="3"
          :content-cols-md="9"
          :labelAlign="labelAlign"
        >
          <ItpSelect2
            name="client"
            v-model="item.partner"
            :value="item.partner"
            @input="
              !($event && typeof $event.stopPropagation === 'function') &&
              (item.partner =
                $event && typeof $event === 'object' && 'data' in $event
                  ? $event.data
                  : $event)
            "
            :dataSource="b_1879559807163442_dataSource"
            bind-object
            valueField="id"
            textField="name"
            required
            optionTemplate="<UcSuggestCompanyOption :option=option></UcSuggestCompanyOption>"
            use-option-template-for-selected-option
          >
          </ItpSelect2>
        </ItpFormGroup>
        <ItpFormGroup
          name="is_discount_price"
          :label="pgettext('ProductPrice', 'Discount price')"
          :label-cols-md="3"
          :content-cols-md="9"
          :labelAlign="labelAlign"
        >
          <ItpFormCheckbox
            name="is_discount_price"
            inline
            v-model="item.is_discount_price"
            :value="item.is_discount_price"
            @input="
              !($event && typeof $event.stopPropagation === 'function') &&
              (item.is_discount_price =
                $event && typeof $event === 'object' && 'data' in $event
                  ? $event.data
                  : $event)
            "
            :readonly="isReadOnly"
          >
          </ItpFormCheckbox>
        </ItpFormGroup>
        <ItpFormGroup
          name="inactive"
          :label="pgettext('ProductPrice', 'Inactive')"
          :label-cols-md="3"
          :content-cols-md="9"
          :labelAlign="labelAlign"
        >
          <ItpFormCheckbox
            name="inactive"
            inline
            v-model="item.inactive"
            :value="item.inactive"
            @input="
              !($event && typeof $event.stopPropagation === 'function') &&
              (item.inactive =
                $event && typeof $event === 'object' && 'data' in $event
                  ? $event.data
                  : $event)
            "
            :readonly="isReadOnly"
          >
          </ItpFormCheckbox>
        </ItpFormGroup>
        <hr />
        <ItpFormGroup
          name="note"
          :label="pgettext('ProductPrice', 'Notes')"
          :label-cols-md="3"
          :content-cols-md="9"
          :labelAlign="labelAlign"
        >
          <ItpFormTextarea
            name="note"
            v-model="item.note"
            :value="item.note"
            @input="
              !($event && typeof $event.stopPropagation === 'function') &&
              (item.note =
                $event && typeof $event === 'object' && 'data' in $event
                  ? $event.data
                  : $event)
            "
            :readonly="isReadOnly"
          >
          </ItpFormTextarea>
        </ItpFormGroup>
      </ItpForm>
    </UcBody>
    <template #footer>
      <UcFooter :card="!modal" :modal="modal" v-if="loaded">
        <ItpBox class="me-auto" v-if="oid && !item.locked">
          <BtnDeleteResource
            :resource="item"
            :disabled="actions.submit.isRunning"
            @success="evh_3322283983417540_success($event, {})"
          >
          </BtnDeleteResource>
          <BtnLockResource
            :resource="item"
            :disabled="actions.submit.isRunning"
            class="ms-3"
            @success="evh_5419443714917073_success($event, {})"
          >
          </BtnLockResource>
        </ItpBox>
        <ItpButton
          :text="pgettext('ModalButton', 'Save')"
          type="submit"
          :form="formId"
          variant="primary"
          icon="fa-save"
          :spinning="actions.submit.isRunning"
          :disabled="!forms[formName] || !forms[formName].submittable"
          v-if="!item.locked"
        >
        </ItpButton>
        <ItpButton
          :text="pgettext('ModalButton', 'Close')"
          variant="default"
          :disabled="actions.submit.isRunning"
          @click.prevent.stop="
            evh_2167826738365259_clickPreventStop($event, {})
          "
        >
        </ItpButton>
      </UcFooter>
    </template>
  </ItpPage>
</template>
